/**
 * Ray's Loyalty — storefront helpers for App Proxy (namespace: RaysLoyalty).
 * Requires snippets/rays-loyalty-config.liquid (window.__RAYS_LOYALTY__).
 */
(function () {
  'use strict';

  function cfg() {
    return window.__RAYS_LOYALTY__ || {};
  }

  function loyaltyBase() {
    // Technical App Proxy paths must never depend on translatable theme values.
    return window.location.origin + '/apps/' + ['rays', 'loyalty'].join('-');
  }

  function withLoggedInCustomerId(url) {
    var id = cfg().customerId;
    if (id == null || id === '') return url;
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return url + sep + 'logged_in_customer_id=' + encodeURIComponent(String(id));
  }

  function buildUrl(segmentAndQuery) {
    var path = String(segmentAndQuery || '').replace(/^\//, '');
    var base = loyaltyBase();
    if (!base) return '/' + path;
    return base + '/' + path;
  }

  /**
   * @param {string} segmentAndQuery e.g. "tier" or "earn?price_cents=1000"
   * @param {RequestInit} [options]
   */
  function storefrontCfg() {
    return window.__RAYS_STOREFRONT__ || {};
  }

  function getStorefrontCustomerAccessToken() {
    var key = String(storefrontCfg().customerTokenStorageKey || 'rays_sfac').trim();
    if (!key) return '';
    try {
      return String(sessionStorage.getItem(key) || '').trim();
    } catch (e) {
      return '';
    }
  }

  /**
   * New Customer Accounts: Liquid `customer` is often empty, but Storefront Web Components
   * may expose the Storefront customer access token on `shopify-store` / `shopify-account`.
   * Sync into sessionStorage so `customer(customerAccessToken:)` works.
   */
  function readCustomerAccessTokenFromShopifyDom() {
    var storeEl = document.querySelector('shopify-store');
    if (storeEl) {
      var storeRaw =
        storeEl.customerAccessToken ||
        (storeEl.dataset && storeEl.dataset.customerAccessToken) ||
        storeEl.getAttribute('customer-access-token') ||
        '';
      storeRaw = String(storeRaw || '').trim();
      if (storeRaw) return storeRaw;
    }
    var tags = ['shopify-store', 'shopify-account'];
    for (var t = 0; t < tags.length; t++) {
      var nodes = document.querySelectorAll(tags[t]);
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        var raw =
          el.customerAccessToken ||
          (el.dataset && el.dataset.customerAccessToken) ||
          el.getAttribute('customer-access-token') ||
          '';
        raw = String(raw || '').trim();
        if (raw) return raw;
      }
    }
    return '';
  }

  function syncStorefrontTokenFromWebComponents() {
    var token = readCustomerAccessTokenFromShopifyDom();
    if (!token) return false;
    var key = String(storefrontCfg().customerTokenStorageKey || 'rays_sfac').trim();
    if (!key) return false;
    try {
      sessionStorage.setItem(key, token);
    } catch (e) {
      return false;
    }
    return true;
  }

  function waitForStorefrontCustomerAccessToken(opts) {
    opts = opts || {};
    var maxAttempts = opts.maxAttempts != null ? opts.maxAttempts : 24;
    var delayMs = opts.delayMs != null ? opts.delayMs : 250;
    return new Promise(function (resolve) {
      var attempt = 0;
      function tick() {
        syncStorefrontTokenFromWebComponents();
        var token = getStorefrontCustomerAccessToken();
        if (token) {
          resolve(token);
          return;
        }
        attempt += 1;
        if (attempt >= maxAttempts) {
          resolve('');
          return;
        }
        window.setTimeout(tick, delayMs);
      }
      tick();
    });
  }

  function storefrontGidToNumericCustomerId(gid) {
    if (!gid) return '';
    var m = String(gid).match(/Customer\/(\d+)/);
    return m ? m[1] : '';
  }

  /**
   * Storefront GraphQL via Ray's Loyalty App Proxy (whitelisted operation). Query arg ignored; server runs fixed query.
   * @param {string} _query unused (kept for call-site compatibility)
   * @param {object} [variables]
   */
  function storefrontGraphql(_query, variables) {
    var proxy = loyaltyBase() + '/storefront-graphql';
    if (!proxy) {
      return Promise.reject(new Error('rays_storefront_proxy_not_configured'));
    }
    return fetch(proxy, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        operation: 'RaysCustomerSummary',
        variables: variables && typeof variables === 'object' ? variables : {},
      }),
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) {
          var err = new Error('rays_storefront_http_' + res.status);
          err.body = body;
          throw err;
        }
        if (body && body.errors && body.errors.length) {
          var ge = new Error('rays_storefront_graphql_errors');
          ge.body = body.errors;
          throw ge;
        }
        return body.data || {};
      });
    });
  }

  var STOREFRONT_CUSTOMER_SUMMARY = [
    'query RaysCustomerSummary($customerAccessToken: String!) {',
    '  customer(customerAccessToken: $customerAccessToken) {',
    '    id',
    '    firstName',
    '    lastName',
    '    displayName',
    '    email',
    '    phone',
    '    defaultAddress { formatted }',
    '    orders(first: 250) {',
    '      edges { node { fulfillmentStatus } }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  function getCustomerStorefrontSummary() {
    var cat = getStorefrontCustomerAccessToken();
    if (!cat) return Promise.resolve(null);
    return storefrontGraphql(STOREFRONT_CUSTOMER_SUMMARY, {
      customerAccessToken: cat,
    }).then(function (data) {
      var c = data && data.customer;
      if (!c) return null;
      var edges = (c.orders && c.orders.edges) || [];
      var all = edges.length;
      var fulfilled = 0;
      for (var i = 0; i < edges.length; i++) {
        var st = String(
          (edges[i].node && edges[i].node.fulfillmentStatus) || ''
        ).toUpperCase();
        if (st === 'FULFILLED') fulfilled += 1;
      }
      var display =
        (c.displayName && String(c.displayName).trim()) ||
        [c.firstName, c.lastName]
          .filter(function (x) {
            return x && String(x).trim();
          })
          .join(' ')
          .trim() ||
        (c.email && String(c.email)) ||
        '';
      var addrLines = '';
      if (c.defaultAddress && c.defaultAddress.formatted) {
        var f = c.defaultAddress.formatted;
        addrLines = Array.isArray(f) ? f.join(', ') : String(f);
      }
      var numericId = storefrontGidToNumericCustomerId(c.id);
      return {
        source: 'storefront',
        shopifyCustomerId: numericId || '',
        displayName: display,
        email: c.email != null ? String(c.email) : '',
        phone: c.phone != null ? String(c.phone) : '',
        defaultAddressFormatted: addrLines,
        numberOfOrders: all,
        fulfilledCount: fulfilled,
        unfulfilledCount: Math.max(0, all - fulfilled),
      };
    });
  }

  function fetchJson(segmentAndQuery, options) {
    options = options || {};
    syncStorefrontTokenFromWebComponents();
    var cat = getStorefrontCustomerAccessToken();
    var url = withLoggedInCustomerId(buildUrl(segmentAndQuery));
    var headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
    if (cat) {
      headers['X-Rays-Customer-Access-Token'] = cat;
    }
    var next = Object.assign({}, options, {
      credentials: options.credentials || 'same-origin',
      headers: headers,
    });
    return fetch(url, next).then(function (res) {
      var ct = res.headers.get('content-type') || '';
      var p = ct.includes('application/json') ? res.json() : res.text();
      return p.then(function (body) {
        if (!res.ok) {
          var err = new Error('rays_loyalty_http_' + res.status);
          err.status = res.status;
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  }

  /**
   * Add free-product variant to cart (when known) and apply discount via Shopify /discount URL.
   * @param {string} discountCode
   * @param {{ variantId?: string|null, productPath?: string|null }|null|undefined} freeProductCart
   */
  function applyFreeProductDiscountToCart(discountCode, freeProductCart) {
    var code = String(discountCode || '').trim();
    if (!code) return Promise.reject(new Error('no_code'));
    var cart = freeProductCart && typeof freeProductCart === 'object' ? freeProductCart : {};
    var variantId =
      cart.variantId != null && cart.variantId !== '' ? String(cart.variantId) : '';
    var productPath =
      cart.productPath != null && cart.productPath !== '' ? String(cart.productPath) : '';

    function redirectWithDiscount(path) {
      var target = path || '/cart';
      window.location.assign(
        '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(target)
      );
    }

    if (variantId) {
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10) || variantId, quantity: 1 }],
        }),
      }).then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) {
            var err = new Error('cart_add_failed');
            err.body = body;
            throw err;
          }
          redirectWithDiscount('/cart');
          return body;
        });
      });
    }

    if (productPath) {
      redirectWithDiscount(productPath);
      return Promise.resolve(null);
    }

    redirectWithDiscount('/cart');
    return Promise.resolve(null);
  }

  function dispatchCustomerIdResolved(id) {
    var next = String(id == null ? '' : id).trim();
    if (!next) return '';
    var root = window.__RAYS_LOYALTY__ || {};
    window.__RAYS_LOYALTY__ = root;
    if (String(root.customerId || '').trim() === next) return next;
    root.customerId = next;
    try {
      document.dispatchEvent(
        new CustomEvent('rays-loyalty:customer-id', { detail: { id: next } })
      );
    } catch (e) {}
    return next;
  }

  function resolveCustomerIdFromTier() {
    return fetchJson('tier')
      .then(function (tr) {
        if (tr && tr.shopifyCustomerId) {
          return dispatchCustomerIdResolved(tr.shopifyCustomerId);
        }
        return '';
      })
      .catch(function () {
        return '';
      });
  }

  /**
   * New Customer Accounts often leave Liquid `customer` empty. Resolve numeric id
   * from storefront token or tier proxy before polling notifications.
   */
  function ensureCustomerId() {
    var root = window.__RAYS_LOYALTY__ || {};
    window.__RAYS_LOYALTY__ = root;
    var existing = String(root.customerId == null ? '' : root.customerId).trim();
    if (existing) return Promise.resolve(existing);

    return waitForStorefrontCustomerAccessToken()
      .then(function () {
        syncStorefrontTokenFromWebComponents();
        existing = String(root.customerId == null ? '' : root.customerId).trim();
        if (existing) return existing;

        if (getStorefrontCustomerAccessToken()) {
          return getCustomerStorefrontSummary()
            .then(function (sum) {
              if (sum && sum.shopifyCustomerId) {
                return dispatchCustomerIdResolved(sum.shopifyCustomerId);
              }
              return resolveCustomerIdFromTier();
            })
            .catch(function () {
              return resolveCustomerIdFromTier();
            });
        }

        return resolveCustomerIdFromTier();
      });
  }

  function copyToClipboard(text) {
    var value = String(text == null ? '' : text);
    if (!value) return Promise.reject(new Error('empty'));
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(value);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) resolve();
        else reject(new Error('copy_failed'));
      } catch (e) {
        reject(e);
      }
    });
  }

  var rlApiPrior = window.RaysLoyalty || {};
  var visitRequest = null;
  var crossTabOwner = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);

  function crossTabStorageKey(name) {
    return 'rays_loyalty_once_v1:' + String(name || 'default');
  }

  function runCrossTabOnce(name, minIntervalMs, task, leaseMs) {
    var key = crossTabStorageKey(name);
    var now = Date.now();
    var interval = Math.max(0, Number(minIntervalMs) || 0);
    var lease = Math.max(3000, Number(leaseMs) || 15000);
    var state = {};
    try {
      state = JSON.parse(localStorage.getItem(key) || '{}') || {};
      if (Number(state.lastRunAt) && now - Number(state.lastRunAt) < interval) return Promise.resolve({ skipped: true, reason: 'recent' });
      if (Number(state.lockedUntil) > now && state.owner !== crossTabOwner) return Promise.resolve({ skipped: true, reason: 'locked' });
      localStorage.setItem(key, JSON.stringify({ owner: crossTabOwner, lockedUntil: now + lease, lastRunAt: Number(state.lastRunAt) || 0 }));
      state = JSON.parse(localStorage.getItem(key) || '{}') || {};
      if (state.owner !== crossTabOwner) return Promise.resolve({ skipped: true, reason: 'contended' });
    } catch (storageError) { return Promise.resolve().then(task); }
    return Promise.resolve().then(task).then(function (result) {
      try { localStorage.setItem(key, JSON.stringify({ owner: '', lockedUntil: 0, lastRunAt: Date.now() })); } catch (e) {}
      return result;
    }, function (error) {
      try { var current = JSON.parse(localStorage.getItem(key) || '{}') || {}; if (current.owner === crossTabOwner) localStorage.removeItem(key); } catch (e) {}
      throw error;
    });
  }

  window.RaysLoyalty = Object.assign(rlApiPrior, {
    loyaltyBase: loyaltyBase,
    withLoggedInCustomerId: withLoggedInCustomerId,
    buildUrl: buildUrl,
    fetchJson: fetchJson,
    copyToClipboard: copyToClipboard,
    applyFreeProductDiscountToCart: applyFreeProductDiscountToCart,
    getTier: function () {
      return fetchJson('tier');
    },
    getLedger: function () {
      return fetchJson('ledger');
    },
    getRewards: function () {
      return fetchJson('rewards');
    },
    getCoupons: function (opts) {
      return fetchJson(opts && opts.includeHistory ? 'coupons?history=1' : 'coupons');
    },
    getStoreCredit: function (opts) {
      opts = opts || {};
      var q = 'store-credit';
      if (opts.locale) {
        q += '?locale=' + encodeURIComponent(String(opts.locale));
      }
      return fetchJson(q);
    },
    getReferralLink: function () {
      return fetchJson('referral-link');
    },
    getEarn: function (priceCents) {
      var q =
        priceCents != null && priceCents !== ''
          ? 'earn?price_cents=' + encodeURIComponent(String(priceCents))
          : 'earn';
      return fetchJson(q);
    },
    postRedeem: function (rewardHandle) {
      return fetchJson('redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rewardHandle: rewardHandle }),
      });
    },
    postCustomerProfileUpdate: function (payload) {
      return fetchJson('customer-profile-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload && typeof payload === 'object' ? payload : {}),
      });
    },
    getCustomerProfile: function () {
      return fetchJson('customer-profile');
    },
    getClubBenefits: function () {
      var monthlyHandle = ['28-off-monthly', 'vip-rewards'].join('-');
      return fetchJson(
        'club-benefits?monthly_handle=' + encodeURIComponent(monthlyHandle)
      );
    },
    postBirthdayClaim: function () {
      return fetchJson('birthday-claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    },
    postVisit: function () {
      if (visitRequest) return visitRequest;

      var request = runCrossTabOnce('visit', 12000, function () {
        return waitForStorefrontCustomerAccessToken()
          .catch(function () { return ''; })
          .then(function () { return ensureCustomerId().catch(function () { return ''; }); })
          .then(function () {
            return fetchJson('visit', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({}),
            });
          });
      }, 20000)
        .then(function (res) {
          if (res && res.skipped) return res;
          try {
            document.dispatchEvent(
              new CustomEvent('rays-loyalty:session-grants', { detail: res })
            );
          } catch (evErr) {}
          return res;
        });

      visitRequest = request.then(
        function (res) {
          visitRequest = null;
          return res;
        },
        function (err) {
          visitRequest = null;
          throw err;
        }
      );

      return visitRequest;
    },
    getRewardNotifications: function (opts) {
      opts = opts || {};
      var q = 'reward-notifications';
      if (opts.unacknowledged) {
        q += '?unacknowledged=1';
      }
      if (opts.limit != null) {
        q += (q.indexOf('?') >= 0 ? '&' : '?') + 'limit=' + encodeURIComponent(String(opts.limit));
      }
      return fetchJson(q);
    },
    ackRewardNotifications: function (ids) {
      return fetchJson('reward-notifications-ack', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: ids || [] }),
      });
    },
    getWelcomeAutoRedeem: function () {
      return fetchJson('welcome-auto-redeem');
    },
    postWelcomeAutoRedeemCancel: function () {
      return fetchJson('welcome-auto-redeem-cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    },
    storefrontGraphql: storefrontGraphql,
    getCustomerStorefrontSummary: getCustomerStorefrontSummary,
    getStorefrontCustomerAccessToken: getStorefrontCustomerAccessToken,
    syncStorefrontTokenFromWebComponents: syncStorefrontTokenFromWebComponents,
    waitForStorefrontCustomerAccessToken: waitForStorefrontCustomerAccessToken,
    ensureCustomerId: ensureCustomerId,
    runCrossTabOnce: runCrossTabOnce,
  });

  if (typeof window.RaysLoyalty.openAccountSheet !== 'function') {
    window.RaysLoyalty.openAccountSheet = function () {
      var url = loginFallbackUrlFromRoutes();
      window.location.assign(url);
      return false;
    };
  }

  function loginFallbackUrlFromRoutes() {
    if (window.routes && window.routes.account_login_url) {
      return String(window.routes.account_login_url).trim() || '/account/login';
    }
    if (window.__RAYS_LOYALTY__ && window.__RAYS_LOYALTY__.loginUrl) {
      return String(window.__RAYS_LOYALTY__.loginUrl).trim() || '/account/login';
    }
    return '/account/login';
  }
})();
